import type { PluginHomeTile } from "@d12labs/plugins/types";
import { KPI } from "@d12labs/ui/kpi";
import { Skeleton } from "@d12labs/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { speechCoachApi } from "./api";

/**
 * Home-page KPI tile for Speech Coach. Self-contained: fetches its own data
 * from GET /api/plugins/speech-coach/summary and returns null when the user
 * has no active plan (hidden per spec).
 */
export const pluginHomeTile: PluginHomeTile = () => {
	const { data, isLoading } = useQuery({
		queryKey: ["speech-coach", "home-summary"],
		queryFn: () => speechCoachApi.summary(),
	});

	if (isLoading) {
		return (
			<div className="rounded-lg border border-hairline bg-surface-1 p-4">
				<Skeleton w="40%" h={12} className="mb-3" />
				<div className="grid grid-cols-3 gap-3">
					<Skeleton h={64} />
					<Skeleton h={64} />
					<Skeleton h={64} />
				</div>
			</div>
		);
	}

	if (!data || data.plan === null) {
		return null;
	}

	const { streak, weeklyMinutes, sessionsThisWeek, plan } = data;
	const leftToTarget = Math.max(0, plan.weeklyTarget - sessionsThisWeek);

	return (
		<Link
			to="/speech-coach"
			className="block rounded-lg border border-hairline bg-surface-1 p-4 transition-colors hover:border-hairline-strong hover:bg-surface-2"
		>
			<p className="mb-3 text-eyebrow uppercase text-ink-tertiary">Speech Coach</p>
			<div className="grid grid-cols-3 gap-3">
				<KPI
					label="Streak"
					value={`${streak} days`}
					accent={streak > 0 ? "text-primary" : undefined}
				/>
				<KPI label="This week" value={`${weeklyMinutes} min`} />
				<KPI label="Left to target" value={leftToTarget} />
			</div>
		</Link>
	);
};
